import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { SignatureSetupCopySource } from '@completion/models';
import { ResourceStatus, ResourceType } from '@completion/enums';
import { SignatureSetupCopy, SignatureSetupCopySourcesLoad } from '@completion/actions';
import { State } from '@completion/reducers';
import { getResourceState, getSignatureSetupCopyResult, getSignatureSetupCopySources } from '@completion/selectors';
import { Store } from '@ngrx/store';
import { ButtonModule } from 'primeng/button';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { SelectModule } from 'primeng/select';
import { Subject, takeUntil } from 'rxjs';

export interface SignatureSetupCopyDialogData {
  targetProjectId: number;
  targetProjectName: string;
}

@Component({
  selector: 'app-signature-setup-copy-dialog',
  imports: [ButtonModule, FormsModule, SelectModule],
  templateUrl: './signature-setup-copy-dialog.component.html',
  styleUrls: ['./signature-setup-copy-dialog.component.scss'],
})
export class SignatureSetupCopyDialogComponent implements OnInit, OnDestroy {
  sources: SignatureSetupCopySource[] = [];
  selectedSourceId: number | null = null;
  isLoadingSources = true;
  isCopying = false;
  sourceLoadError: string | null = null;
  copyError: string | null = null;
  private copyRequested = false;
  private readonly destroy$ = new Subject<void>();

  private readonly store = inject(Store<State>);
  private readonly dialogRef = inject(DynamicDialogRef);
  private readonly config = inject(DynamicDialogConfig);

  get data(): SignatureSetupCopyDialogData {
    return this.config.data as SignatureSetupCopyDialogData;
  }

  ngOnInit(): void {
    this.store
      .select(getSignatureSetupCopySources)
      .pipe(takeUntil(this.destroy$))
      .subscribe((sources) => (this.sources = sources));

    this.store
      .select(getResourceState, ResourceType.GetSignatureSetupCopySources)
      .pipe(takeUntil(this.destroy$))
      .subscribe((resource) => {
        this.isLoadingSources = resource.status === ResourceStatus.InProgress;
        if (resource.status === ResourceStatus.Failure) {
          this.sourceLoadError = resource.lastError;
        } else if (resource.status === ResourceStatus.InProgress || resource.status === ResourceStatus.Success) {
          this.sourceLoadError = null;
        }
      });

    this.store
      .select(getResourceState, ResourceType.CopySignatureSetup)
      .pipe(takeUntil(this.destroy$))
      .subscribe((resource) => {
        this.isCopying = resource.status === ResourceStatus.InProgress;
        if (resource.status === ResourceStatus.Failure) {
          this.copyError = resource.lastError;
        } else if (resource.status === ResourceStatus.InProgress || resource.status === ResourceStatus.Success) {
          this.copyError = null;
        }
      });

    this.store
      .select(getSignatureSetupCopyResult)
      .pipe(takeUntil(this.destroy$))
      .subscribe((result) => {
        if (this.copyRequested && result) {
          this.dialogRef.close(result);
        }
      });

    this.isLoadingSources = true;
    this.sourceLoadError = null;
    this.store.dispatch(new SignatureSetupCopySourcesLoad(this.data.targetProjectId));
  }

  copy(): void {
    if (this.selectedSourceId === null || this.isCopying) {
      return;
    }

    this.copyError = null;
    this.copyRequested = true;
    this.store.dispatch(new SignatureSetupCopy(this.data.targetProjectId, this.selectedSourceId));
  }

  cancel(): void {
    if (!this.isCopying) {
      this.dialogRef.close(null);
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
